/**
 * Cloudflare Turnstile — loaded dynamically (no inline scripts) and rendered
 * explicitly into the step-4 container so the token is fresh at submit time
 * (tokens are single-use and expire after ~5 minutes).
 */

interface TurnstileApi {
  render(
    container: string | HTMLElement,
    params: {
      sitekey: string
      callback?: (token: string) => void
      'expired-callback'?: () => void
      'error-callback'?: () => void
    },
  ): string
  reset(widgetId?: string): void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
    __kcTurnstileOnLoad?: () => void
  }
}

const API_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__kcTurnstileOnLoad&render=explicit'

let enabled = false
let siteKey = ''
let apiReady = false
let renderRequested = false
let widgetId: string | null = null
let currentToken = ''
let onChange: ((hasToken: boolean) => void) | null = null

/**
 * Register a listener notified whenever the token becomes available (challenge
 * solved) or is cleared (expired / errored / reset). The submit flow uses this
 * to keep the submit button locked until the visitor completes the widget.
 */
export function onTurnstileChange(cb: (hasToken: boolean) => void): void {
  onChange = cb
}

/** Load the Turnstile API script (widget renders later, on step 4). */
export function initTurnstile(key: string): void {
  if (!key) return // no site key configured (e.g. bare local dev) — server still enforces
  enabled = true
  siteKey = key
  // Turnstile is now active but unsolved — push the initial locked state to any
  // listener (the submit button), since this runs after the form is wired up.
  onChange?.(false)

  window.__kcTurnstileOnLoad = () => {
    apiReady = true
    if (renderRequested) renderWidget()
  }

  const script = document.createElement('script')
  script.src = API_SRC
  script.async = true
  script.defer = true
  document.head.appendChild(script)
}

/**
 * Render the widget when the user reaches the review step. Rendering into the
 * then-visible container avoids display:none sizing issues, and a late render
 * keeps the (single-use, ~5 min) token fresh relative to submission.
 */
export function ensureTurnstileRendered(): void {
  if (!enabled || widgetId !== null) return
  renderRequested = true
  if (apiReady) renderWidget()
}

function renderWidget(): void {
  if (widgetId !== null) return
  const container = document.getElementById('turnstileContainer')
  if (!container || !window.turnstile) return
  widgetId = window.turnstile.render(container, {
    sitekey: siteKey,
    callback: (token) => {
      currentToken = token
      onChange?.(true)
    },
    'expired-callback': () => {
      currentToken = ''
      onChange?.(false)
    },
    'error-callback': () => {
      currentToken = ''
      onChange?.(false)
    },
  })
}

export function isTurnstileEnabled(): boolean {
  return enabled
}

export function getTurnstileToken(): string {
  return currentToken
}

/** Tokens are single-use — reset after any failed submit attempt. */
export function resetTurnstile(): void {
  currentToken = ''
  onChange?.(false)
  if (widgetId !== null) window.turnstile?.reset(widgetId)
}
